import { useEffect } from 'react';
import { Alert, AlertTitle, Grid } from '@mui/material';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import useRoles from 'common/hooks/useRoles';
import WorkQueues from 'enums/workQueue.enum';
import { listNonRoutableRequestsByPage } from 'store/thunks/workQueueThunks';

import { useWorkQueueTabSwitch } from '../contexts/WorkQueueTabSwitchContext';
import NonRoutableRequestsQueueFilterButton from './NonRoutableRequestsQueueFilterButton';
import NonRoutableRequestsQueueFilterStatusBar from './NonRoutableRequestsQueueFilterStatusBar';
import NonRoutableRequestsQueueTable from './NonRoutableRequestsQueueTable';

/**
 * Non-Routable Orders holding queue (POM-6223). SYS_ADMIN only.
 *
 * EVERY ROW IS A PRESCRIPTION THAT IS NOT BEING FILLED. Parking writes no rx_request, rx_order,
 * rx_request_line_item or prescription row and notifies no partner, so this screen is the only
 * place anyone at PerfectRx can see that the request exists, and the only route by which a human
 * acts on it.
 *
 * THE ROLE IS RE-CHECKED HERE, not only on the tab. `resetAppState` (store/thunks/authThunks.js)
 * clears auth, patient, partner, pharmacy, orders and websocket state but NOT `workQueue`, so a
 * previously-selected preset survives a sign-out into the next session. The tab list is gated by
 * `accessRoleName`, but the panel itself is mounted from the surviving preset — so without this
 * second check a non-admin could land on a rendered holding queue.
 */
const NonRoutableRequestsQueue = () => {
  const dispatch = useDispatch();
  const { viewNonRoutableOrders } = useRoles();
  const preset = useSelector(({ workQueue }) => workQueue.preset);
  const { currentPage } = useSelector(
    ({ workQueue }) => workQueue.nonRoutableRequests,
    shallowEqual
  );
  const { isTabSwitch } = useWorkQueueTabSwitch();

  const isActive = preset === WorkQueues.NON_ROUTABLE_REQUESTS && viewNonRoutableOrders;

  useEffect(() => {
    if (!isActive) return;
    if (isTabSwitch(WorkQueues.NON_ROUTABLE_REQUESTS)) {
      dispatch(listNonRoutableRequestsByPage({ page: currentPage }));
    }
  }, [dispatch, currentPage, isTabSwitch, isActive]);

  if (preset !== WorkQueues.NON_ROUTABLE_REQUESTS) {
    return null;
  }

  // Refused loudly rather than silently rendering nothing: a blank panel where a queue should be
  // is indistinguishable from an empty queue, and on this screen those mean opposite things.
  if (!viewNonRoutableOrders) {
    return (
      <Grid container sx={{ py: 3 }}>
        <Grid item xs={12}>
          <Alert severity='warning' data-testid='non-routable-queue-forbidden'>
            <AlertTitle>You do not have access to the Non-Routable Orders queue</AlertTitle>
            This queue is restricted to system administrators. Nothing is shown here — that is not
            the same as nothing being parked.
          </Alert>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container sx={{ py: 2 }} alignItems='center' spacing={1}>
      <Grid item sx={{ minHeight: 40, flexGrow: 2 }}>
        <NonRoutableRequestsQueueFilterStatusBar />
      </Grid>
      <Grid item>
        <NonRoutableRequestsQueueFilterButton />
      </Grid>
      <NonRoutableRequestsQueueTable />
    </Grid>
  );
};

export default NonRoutableRequestsQueue;
